import { PaginationReturn } from '@common/types/service';
import { CloseButton } from '@components/utility/buttons';
import { EventListCard } from '@event/components/display/cards/EventListCard';
import { useGetCurrentEventRequiredEventsQuery } from '@event/services/events';
import { EventTypes, RegistrationEventListItem } from '@event/types/event';
import { useAuth } from '@hooks/useAuth';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { CircularProgress, Dialog, DialogContent, DialogProps, DialogTitle, Typography } from '@mui/material';
import { useGetMarketsQuery } from '@services/markets';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

const MIN_ITEMS_THRESHOLD = 4;

const RequiredEventsDialog = (props: DialogProps) => {
    const { data: markets } = useGetMarketsQuery(undefined, {
        skip: !props.open,
    });
    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const { user } = useAuth();
    const [page, setPage] = useState(1);

    const {
        data: eventsResponse,
        isFetching,
        isLoading,
        isSuccess,
    } = useGetCurrentEventRequiredEventsQuery(
        {
            id: Number(eventId),
            type: eventType,
            page_size: 10,
            page,
        },
        {
            skip: !props.open,
        }
    );

    const events = useMemo(() => eventsResponse as PaginationReturn<RegistrationEventListItem>, [eventsResponse]);

    const scrollRef = useRef<HTMLElement>(null);
    const { items } = useInfiniteScroll({
        scrollElement: scrollRef.current,
        data: {
            results: events?.results ?? [],
            count: Number(events?.count),
            isFetching,
        },
        shouldPopulate: isSuccess && !!events.results.length,
        pageState: [page, setPage],
    });

    const showSpinner = isLoading || (isFetching && events?.results.length > MIN_ITEMS_THRESHOLD);

    return (
        <Dialog {...props} classes={{ paper: 'dialog-paper px-[10px] sm:px-[27px]' }}>
            <CloseButton onClick={() => props.onClose?.({}, 'backdropClick')} />
            <DialogTitle align="center">
                <Typography fontFamily="museo-sans" fontSize={32} letterSpacing={-0.8}>
                    <FormattedMessage id="event.badge.required" defaultMessage="Required Events" />
                </Typography>
                <Typography fontSize={16} letterSpacing={0} mt={1}>
                    <FormattedMessage
                        id="event.badge.required.description"
                        defaultMessage="Earn the required badge by completing one of these events."
                    />
                </Typography>
            </DialogTitle>
            <DialogContent ref={scrollRef} className="p-0">
                {items.length > 0 ? (
                    <div className="flex flex-col gap-4 h-full relative sm:gap-2.5 p-1">
                        {items.map((event) => (
                            <EventListCard
                                key={`required-event-${event.type}-${event.id}`}
                                event={event}
                                markets={markets}
                                user={user}
                                target="_blank"
                            />
                        ))}
                    </div>
                ) : !showSpinner ? (
                    <div className="m-auto w-fit mt-10">
                        <p className="bg-charleston-green-10 p-4 rounded font-medium text-base text-dark-jungle-green">
                            <FormattedMessage id="event.noEvents" defaultMessage="No events found" />
                        </p>
                    </div>
                ) : null}
                {showSpinner ? (
                    <div className="flex items-center justify-center min-h-[100px]">
                        <CircularProgress />
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export { RequiredEventsDialog };
